// Today (design/screens/today.html, today-mobile.html at phone width; UC-APPOINTMENTS-006): the day's figures and list from
// `GET /appointments/today` — booked, arrived, in consultation, completed, still to come, no-shows — with the front desk's next
// action on each row (arrive, start, complete: UC-APPOINTMENTS-009, 010) and the unmatched-bookings count. The money, lab and
// sync panels of the prototype stay honest empty states until their sprints. "New patient" hands the tablet over (UC-PATIENTS-022)
// once staff confirm it — or opens the staff form instead, "Type the details yourself" (A10; BR-PATIENTS-041, DVN's checklist D2);
// what the kiosk sends waits in the Pending registrations panel for a reviewer (UC-PATIENTS-024). Needs attention carries the
// lines the built modules can fill: the dependants turned 18 (UC-PATIENTS-011's worklist), the frames below their re-order level
// (UC-INVENTORY-001's trigger, Sprint 4) and the recalls due (UC-APPOINTMENTS-013).
// "Book in Zoho Bookings" and a no-show's "Rebook" open the branch's booking page an Admin typed on the mapping (D63), and show
// only when the answer carries it — never the generic Zoho app. A patient with no consent on record, or one on replaced wording,
// is named so beside them, and checking them in asks for the consent at the desk or on the kiosk (UC-GOVERNANCE-017,
// BR-GOVERNANCE-027) — nothing waits for it.
import { TODAY_COUNT_CAP, type AppointmentView, type TodayView } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { appointments, recalls, type TransitionSegment } from "../api/endpoints.appointments";
import { stock } from "../api/endpoints.inventory";
import { describeError, isApiError } from "../api/problem";
import { DOT, formatLongDate, formatShortDate, formatTime, isLocalDate, localDate } from "../lib/format";
import { ConfirmHandOver } from "../kiosk/ConfirmHandOver";
import { enterKiosk } from "../kiosk/mode";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Avatar, Chip, Empty, ErrorAlert, Loading, Panel } from "../ui/bits";
import { Icon } from "../ui/icons";
import { ApptStatusChip, ApptWarnings, initialsOfDisplay, nextAction, patientSub, recallNotice, showAnswer, useNarrow } from "./AppointmentsBits";
import { ConsentChip, consentArrivalNotice } from "./AppointmentsConsent";
import { andList } from "./CatalogueShared";
import { NoAccessPanel, NoActiveBranch, PageHead } from "./common";
import { WalkInButton } from "./AppointmentsWalkIn";
import { PendingRegistrationsPanel } from "./Registrations";
import { formatDay, recallWindow } from "./Recalls";


export function Today() {
  const s = useSessionView();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const block = useWriteBlock();
  const online = block === null;
  const narrow = useNarrow();
  const dateParam = params.get("date");
  const date = isLocalDate(dateParam) ? dateParam : undefined;
  const day = date ? new Date(`${date}T12:00:00.000Z`) : new Date();
  const branch = s?.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s?.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? "No branch";
  const today = useQuery({ queryKey: ["today", s?.activeBranchId ?? "", date ?? ""], queryFn: () => appointments.today(date ? { date } : {}), enabled: !!s?.activeBranchId, refetchInterval: 60_000 });
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // New patient asks before the device is handed over (BR-PATIENTS-041).
  const [askingHandOver, setAskingHandOver] = useState(false);
  const view = today.data?.data ?? null;
  const rows = view?.appointments ?? [];
  const f = view?.figures;
  // The staff with appointments today ("Dr Naledi Khumalo consulting"); the view names staff, not roles, so two are named and more are counted.
  const consultingStaff = [...new Map(rows.filter((a) => a.optometrist).map((a) => [a.optometrist!.id, a.optometrist!.displayName])).values()];
  const consulting = consultingStaff.length === 0 ? null : consultingStaff.length <= 2 ? `${consultingStaff.join(" and ")} consulting` : `${consultingStaff.length} staff consulting`;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["today"] }); void qc.invalidateQueries({ queryKey: ["calendar"] }); };

  const newPatient = async () => {
    if (!s) return;
    setBusy("kiosk");
    setNotice(null);
    try { await enterKiosk({ formType: "Registration", returnTo: "/today", session: s }); navigate("/kiosk"); }
    catch (err) { setNotice(<Alert tone="crit"><b>Could not start the kiosk.</b> {describeError(err)}</Alert>); }
    finally { setBusy(null); }
  };
  const act = async (a: AppointmentView, segment: TransitionSegment) => {
    setBusy(a.id);
    setNotice(null);
    try {
      const { data } = await appointments.transition(a.id, segment);
      const warned = data.warnings.length > 0 ? <Alert tone={data.warnings.some((w) => w.code === "patient_dormant") ? "warn" : "crit"}><b>{a.patient.displayName}:</b> {data.warnings.map((w) => w.message).join(" ")}{data.warnings.some((w) => w.prompt === "reactivate") ? " Reactivate the account from the patient record." : ""}</Alert> : null;
      // UC-GOVERNANCE-017 step 4: the check-in went ahead; a patient without a current consent is asked for it now.
      const ask = segment === "arrive" ? consentArrivalNotice(data, "/today", setNotice) : null;
      // UC-APPOINTMENTS-010 step 2: a completion says the recall due date it set, after any warning.
      const recall = recallNotice(data);
      // The row shows the answer's status and next action at once; the re-read below brings the figures (UC-APPOINTMENTS-009 step 3).
      showAnswer(qc, data);
      if (warned || ask || recall) setNotice(<>{warned}{ask}{recall}</>);
      refresh();
    } catch (err) {
      setNotice(<Alert tone="crit"><b>Could not update {a.patient.displayName}.</b> {isApiError(err) && err.code === "ILLEGAL_TRANSITION" ? `The appointment is already ${String(err.meta.from ?? a.status).toLowerCase()}.` : describeError(err)}</Alert>);
      refresh();
    } finally {
      setBusy(null);
    }
  };
  const rowAction = (a: AppointmentView) => {
    const next = nextAction(a);
    if (next && can(s, "appointments.appointment.write")) return <button className="btn sm" type="button" disabled={!online || busy === a.id} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void act(a, next.segment)}>{busy === a.id ? "…" : next.label}</button>;
    // Rebook opens the booking in Zoho Bookings only when the answer names where (UC-APPOINTMENTS-007 A3): no generic fallback.
    if (a.status === "NoShow" && a.bookingsUrl) return <a className="btn sm" href={a.bookingsUrl} target="_blank" rel="noopener noreferrer">Rebook</a>;
    return null;
  };

  return (
    <section className="screen" id="s-today">
      <div className="content">
        <PageHead
          title="Today"
          sub={`${formatLongDate(day)} · ${branch}${consulting ? ` · ${consulting}` : ""}`}
          actions={<>
            <WalkInButton staff={view?.assignableStaff ?? []} onDone={(n) => setNotice(n)} />
            {can(s, "patients.registration.handover") && <button className="btn" type="button" disabled={!online || busy === "kiosk"} title={block ? WRITE_BLOCK_TITLE[block] : "Hand this device to a new patient to fill in their details"} onClick={() => setAskingHandOver(true)}><Icon name="plus" />{busy === "kiosk" ? "Starting…" : "New patient"}</button>}
            {askingHandOver && <ConfirmHandOver onCancel={() => setAskingHandOver(false)} onHandOver={() => { setAskingHandOver(false); void newPatient(); }} typeDetailsAt={can(s, "patients.patient.write") ? "/patients/new" : undefined} />}
            <button className="btn primary" type="button" disabled title={online ? "Orders arrive in Sprint 5" : "Unavailable offline"}><Icon name="plus" />New order</button>
          </>}
        />
        {notice}
        <div className="stats">
          <div className="stat"><span className="v faint">—</span><span className="l">Turnover today</span><span className="d">arrives with invoicing in Sprint 6</span></div>
          <div className="stat"><span className="v">{f ? f.completed : "—"}</span><span className="l">Patients seen</span><span className="d">{f ? `${f.inConsultation} in consultation · ${f.toCome} still to come` : "completed appointments"}</span></div>
          <div className="stat"><span className="v faint">—</span><span className="l">Average revenue per patient today</span><span className="d">arrives with reports in Sprint 14</span></div>
          <div className="stat"><span className="v faint">—</span><span className="l">Ready for collection</span><span className="d">arrives with lab orders in Sprint 7</span></div>
          <div className="stat"><span className="v faint">—</span><span className="l">Cash today</span><span className="d">arrives with cash-up in Sprint 13</span></div>
        </div>

        <div className="split wide">
          <div className="stack gap-16">
            <Panel title="Appointments" meta={f ? `${f.booked} booked${DOT}${f.arrived} arrived${DOT}${f.noShows} no-show${DOT}${f.toCome} to come` : "for this branch and day"} tight
              head={<span className="right row c gap-8">
                {view && view.inboxCount > 0 && <Link className="btn sm" to="/appointments?inbox=1">{view.inboxCount} booking{view.inboxCount === 1 ? "" : "s"} to match</Link>}
                {view?.bookingsUrl && <a className="btn sm" href={view.bookingsUrl} target="_blank" rel="noopener noreferrer">Book in Zoho Bookings</a>}
                <Link className="btn sm" to={date ? `/appointments?date=${date}` : "/appointments"}>Open calendar</Link>
              </span>}>
              {/* No active branch — a new organisation without branches, a user assigned to none: the day is never asked for, so it
                  is said rather than shown as loading for good (UC-APPOINTMENTS-006 A5). */}
              {!s?.activeBranchId && <NoActiveBranch what="the day's appointments" />}
              {today.isPending && today.fetchStatus !== "idle" && <Loading what="appointments" />}
              {today.isError && (isApiError(today.error) && today.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Appointments" error={today.error} /> : <ErrorAlert error={today.error} />)}
              {view && rows.length === 0 && <Empty title="No appointments today.">Bookings made in Zoho Bookings appear here within five minutes; record a patient who arrives without one with Walk-in.</Empty>}
              {view && rows.length > 0 && (narrow ? (
                <div className="list today-list">
                  {rows.map((a) => (
                    <div key={a.id} className="item">
                      <span className="t">{formatTime(a.startAt)}</span>
                      <div className="grow">
                        <div className="primary"><Link to={`/patients/${a.patientId}`}>{a.patient.displayName}</Link></div>
                        <div className="sub"><span className="mono">{patientSub(a)}</span> · {a.appointmentType.name} · {a.optometrist?.displayName ?? "Unassigned"}</div>
                        <ApptWarnings warnings={a.warnings} />
                        <ConsentChip a={a} />
                      </div>
                      <ApptStatusChip status={a.status} />
                      {rowAction(a)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="tbl-scroll">
                  <table>
                    {/* "Seen by", not "Optometrist": a frame consultant or a dispenser sees a fitting (UC-APPOINTMENTS-006 step 3). */}
                    <thead><tr><th>Time</th><th>Patient</th><th>Type</th><th>Seen by</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {rows.map((a) => (
                        <tr key={a.id} className={a.status === "InProgress" ? "sel" : undefined}>
                          <td className="mono">{formatTime(a.startAt)}</td>
                          <td><div className="name-cell"><Avatar initials={initialsOfDisplay(a.patient.displayName)} size="sm" /><div><Link to={`/patients/${a.patientId}`}>{a.patient.displayName}</Link><div className="sub mono">{patientSub(a)}</div><ApptWarnings warnings={a.warnings} /><ConsentChip a={a} /></div></div></td>
                          <td>{a.appointmentType.name}{a.typeUnmapped && <span className="small faint"> · unmapped service</span>}</td>
                          <td>{a.optometrist?.displayName ?? <span className="faint">Unassigned</span>}</td>
                          <td><ApptStatusChip status={a.status} />{a.awaitingApproval && <div className="small faint">awaiting approval in Bookings</div>}</td>
                          <td className="r">{rowAction(a)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </Panel>
            <Panel title="Ready for collection" meta="lab received" tight>
              <Empty title="Collections arrive with lab orders in Sprint 7." />
            </Panel>
          </div>

          <div className="stack gap-16">
            <PendingRegistrationsPanel onNotice={setNotice} />
            <NeedsAttention view={view} dayPending={today.isPending && today.fetchStatus !== "idle"} branch={branch} />
            <Panel title="Payments today" meta="by method" tight>
              <Empty title="Payments arrive with invoicing in Sprint 6." />
            </Panel>
            <Panel title="Sales by staff" meta="month to date vs monthly target" tight>
              <Empty title="Sales targets arrive with reports in Sprint 14." />
            </Panel>
          </div>
        </div>
        <span className="help">Today shows {formatShortDate(day)} at {branch}. Bookings are made in Zoho Bookings; Vision X reads them (D23).{view ? (view.lastPullAt ? ` Last pull ${formatTime(view.lastPullAt)}.` : " No pull has run for this branch yet.") : ""}</span>
      </div>
    </section>
  );
}

/** The words of up to four names, the rest counted: "A · B · C · D · and 2 more". */
const namesOf = (names: readonly string[], count: number): string[] => [...names.slice(0, 4), ...(count > Math.min(names.length, 4) ? [`and ${count - Math.min(names.length, 4)} more`] : [])];
/** "a", "a or b", "a, b or c". */
const orList = (items: readonly string[]): string => (items.length > 1 ? `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}` : items[0] ?? "");

/**
 * Needs attention (today.html): one line per worklist with something in it, the count in a chip, what it is, and the way in —
 * the dependants turned 18 whose home branch is this one (TodayView's `dependentsTurned18`, UC-PATIENTS-011; the worklist opens
 * on the same branch and nothing is detached by itself), the frames below their re-order level at the active branch, named, from
 * the stock summary the Stock page's stats read (UC-INVENTORY-001's trigger, BR-INVENTORY-022; every role reads stock) with the
 * way into Stock below re-order — the other lines below theirs said beside them, or alone when no frame is below — and the
 * recalls due over the Recalls page's opening window at the active branch (UC-APPOINTMENTS-013), so each number is the rows its
 * page opens with. The lab, cash-up and sync lines of the prototype arrive with their modules.
 */
function NeedsAttention({ view, dayPending, branch }: { view: TodayView | null; dayPending: boolean; branch: string }) {
  const s = useSessionView();
  const branchId = s?.activeBranchId ?? undefined;
  const span = recallWindow(localDate(new Date()));
  const due = useQuery({
    queryKey: ["attention", "recalls", branchId ?? "", span.from, span.to],
    queryFn: () => recalls.list({ from: span.from, to: span.to, branchId, limit: 1, count: true }),
    enabled: can(s, "appointments.recall.read") && !!branchId, refetchInterval: 5 * 60_000,
  });
  const summary = useQuery({
    queryKey: ["attention", "stock", branchId ?? ""],
    queryFn: () => stock.summary(branchId),
    enabled: can(s, "inventory.stock.read") && !!branchId, refetchInterval: 5 * 60_000,
  });
  const turnedCount = view?.dependentsTurned18 ?? 0;
  const dueCount = due.data?.data.totalCount ?? due.data?.data.items.length ?? 0;
  const below = summary.data?.data.belowReOrder;
  const lines: ReactNode[] = [];
  if (turnedCount > 0 && can(s, "patients.patient.read")) {
    lines.push(
      <div key="turned-18" className="item">
        <Chip tone="warn" dot>{turnedCount >= TODAY_COUNT_CAP ? `${TODAY_COUNT_CAP}+` : turnedCount}</Chip>
        <div className="grow"><div className="primary">{turnedCount === 1 ? "Dependant turned 18" : "Dependants turned 18"}</div><div className="sub">Home branch {branch} · detach or keep each one; nobody is detached automatically</div></div>
        <Link className="btn sm" to={branchId ? `/patients/turned-18?branch=${branchId}` : "/patients/turned-18"}>Review</Link>
      </div>,
    );
  }
  if (below && below.frames + below.other > 0) {
    const frames = below.frames > 0;
    const others = frames && below.other > 0 ? [below.other <= 2 ? `plus ${andList(below.otherNames)}` : `plus ${below.other} other lines`] : [];
    const words = frames ? [...namesOf(below.frameNames, below.frames), ...others] : namesOf(below.otherNames, below.other);
    lines.push(
      <div key="re-order" className="item">
        <Chip tone="warn" dot>{frames ? below.frames : below.other}</Chip>
        <div className="grow"><div className="primary">{frames ? (below.frames === 1 ? "Frame below re-order level" : "Frames below re-order level") : "Stock below re-order level"}</div><div className="sub">{words.join(" · ")}</div></div>
        <Link className="btn sm" to="/stock?below=true">Stock</Link>
      </div>,
    );
  }
  if (dueCount > 0) {
    lines.push(
      <div key="recalls" className="item">
        <Chip tone="info" dot>{dueCount}</Chip>
        <div className="grow"><div className="primary">{dueCount === 1 ? "Recall due" : "Recalls due"}</div><div className="sub">Due {formatDay(span.from)} – {formatDay(span.to)} · phone, snooze or dismiss · messages from Sprint 6</div></div>
        <Link className="btn sm" to="/appointments/recalls">Recalls</Link>
      </div>,
    );
  }
  const loading = dayPending || (due.isPending && due.fetchStatus !== "idle") || (summary.isPending && summary.fetchStatus !== "idle");
  // What an empty panel may truthfully say was checked: only the reads this role has and that answered.
  const checked = [...(can(s, "patients.patient.read") && view ? ["dependants turned 18"] : []), ...(due.isSuccess ? ["recalls due"] : []), ...(summary.isSuccess ? ["stock below re-order"] : [])];
  const later = "Overdue lab orders, missed cash-ups and sync failures arrive with their modules (Sprints 7–13).";
  return (
    <Panel title="Needs attention" meta={lines.length > 0 ? `${lines.length} item${lines.length === 1 ? "" : "s"}` : undefined} tight foot={lines.length > 0 ? <span className="help">{later}</span> : undefined}>
      {loading && lines.length === 0 && <Loading what="worklists" />}
      {due.isError && <div className="panel-body"><ErrorAlert error={due.error} /></div>}
      {summary.isError && <div className="panel-body"><ErrorAlert error={summary.error} /></div>}
      {lines.length > 0 ? <div className="list">{lines}</div> : !loading && <Empty title="Nothing needs attention.">{checked.length > 0 ? `No ${orList(checked)} at ${branch}. ` : ""}{later}</Empty>}
    </Panel>
  );
}
